import { useEffect, useMemo, useRef, useState } from 'react';
import { api, darf, datum, heuteIso, tagIso, uhr, zeitpunkt, type Anmelder }
  from '../api.ts';
import { Kopf } from '../teile/Geruest.tsx';
import { Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import Mailfach from '../teile/Mailfach.tsx';
import { Dateikachel, type Kacheldatei } from '../teile/Dateikachel.tsx';
import { useDateifenster } from '../teile/Dateifenster.tsx';
import { Klammer } from '../teile/Klammer.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { ZuordnenKnopf } from '../teile/AuftragSuche.tsx';
import MailSchreiben, { Adressfeld, adressen, useVorlagen } from '../teile/MailSchreiben.tsx';

/**
 * DAS POSTFACH ALS ARBEITSPLATZ (.285).
 * ===========================================================================
 *
 *   ORIGINALTEXT MARVIN (02.10.2026, mit einem Bild aus einem CRM)
 *     „ich finde unser menüpunkt mit dem postächern sehr unübersichtlicht,
 *      ich kann da überhaupt keine emails raus senden, das thema kommentare
 *      und tags geht irgendwie unter. baue diesen bereich komplett um und
 *      erstelle was neues dynamisches innovatives, womit man arbeit kann."
 *
 * DIE EINHEIT IST DER VORGANG, NICHT DIE MAIL. Ein Faden mit Mails rein und
 * raus, Notizen und jedem Handgriff steht als EINE Zeitachse da - wie auf
 * Marvins Bild, und nicht als Mail, darunter eine Karte „Notizen", darunter
 * eine Karte „Vorgang". Was am Vorgang hängt (Auftrag, Zuständig, Tags,
 * Wiedervorlage, Beteiligte, Anhänge), steht rechts daneben.
 *
 * DREI BEREICHE BLEIBEN DREI BEREICHE: links die Ansichten, Tags und
 * Postfächer, in der Mitte die Liste, rechts der Vorgang. Auf dem Telefon
 * zwei Schritte, wie bisher.
 *
 * DIE TASTATUR ARBEITET MIT: j und k blättern, r antworten, n Notiz,
 * e erledigt, c neue Mail, / suchen.
 */

const ANSICHTEN: { schluessel: string; name: string; zeichen: string; hilfe: string }[] = [
  { schluessel: 'eingang', name: 'Posteingang', zeichen: '⬇', hilfe: 'Alles mit offenen eingegangenen Mails.' },
  { schluessel: 'ohne_auftrag', name: 'Ohne Auftrag', zeichen: '!', hilfe: 'Offen und noch keinem Auftrag zugeordnet.' },
  { schluessel: 'mir', name: 'Mir zugewiesen', zeichen: '◉', hilfe: 'Vorgänge, für die du zuständig bist.' },
  { schluessel: 'erwaehnt', name: 'Erwähnungen', zeichen: '@', hilfe: 'Vorgänge, in denen dich jemand mit @ genannt hat.' },
  { schluessel: 'wiedervorlage', name: 'Wiedervorlage', zeichen: '⏰', hilfe: 'Heute oder früher zur Wiedervorlage.' },
  { schluessel: 'gesendet', name: 'Gesendet', zeichen: '⬆', hilfe: 'Zuletzt ging etwas von uns hinaus.' },
  { schluessel: 'erledigt', name: 'Erledigt', zeichen: '✓', hilfe: 'Abgehakt.' },
  { schluessel: 'alle', name: 'Alle', zeichen: '≡', hilfe: 'Alles.' },
];

const FARBEN = ['#0969da', '#1a7f37', '#9a6700', '#cf222e', '#8250df', '#bf3989', '#0a7ea4', '#bc4c00'];
function farbeVon(s: string): string {
  let h = 0;
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return FARBEN[h % FARBEN.length]!;
}
function kuerzel(name: string): string {
  const t = name.replace(/<[^>]*>/g, '').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return ((t[0]?.[0] ?? '?') + (t.length > 1 ? t[t.length - 1]![0] : '')).toUpperCase();
}
export function Rund({ name, klein }: { name: string; klein?: boolean }) {
  return (
    <span className={'postrund' + (klein ? ' klein' : '')} style={{ background: farbeVon(name) }}
          title={name} aria-hidden="true">{kuerzel(name)}</span>
  );
}

function wer(z: any): string {
  if (z.letzte_richtung === 'aus' || z.richtung === 'aus') return 'an ' + ((z.empfaenger ?? [])[0] ?? '').split('@')[0];
  return z.absender_name || (z.absender ?? '').replace(/<.*>/, '').split('@')[0] || 'ohne Absender';
}

/** Heute die Uhrzeit, sonst der Tag - in deutscher Zeit. */
function wann(w: string | null): string {
  if (!w) return '';
  const d = new Date(w);
  if (Number.isNaN(d.getTime())) return '';
  return tagIso(d) === heuteIso() ? uhr(d) : datum(d);
}

/** „vor 5 Min." für die Zeitachse, mit dem genauen Zeitpunkt im Titel. */
function vorWann(w: string): string {
  const s = (Date.now() - new Date(w).getTime()) / 1000;
  if (s < 60) return 'gerade eben';
  if (s < 3600) return `vor ${Math.floor(s / 60)} Min.`;
  if (s < 86400 && tagIso(w) === heuteIso()) return `vor ${Math.floor(s / 3600)} Std.`;
  return zeitpunkt(w);
}

function Tagchip({ name, farbe, weg }: { name: string; farbe?: string; weg?: () => void }) {
  return (
    <span className="posttag" style={{ borderColor: farbe ?? '#6e7781', color: farbe ?? '#6e7781' }}>
      {name}
      {weg && <button type="button" onClick={weg} aria-label={`Tag ${name} entfernen`}>×</button>}
    </span>
  );
}

export default function Postfach(
  { ich, oeffne }:
  { ich: Anmelder; oeffne: (auftragId: string) => void }
) {
  const [ansicht, setAnsicht] = useState('eingang');
  const [tag, setTag] = useState('');
  const [konto, setKonto] = useState('');
  const [ort, setOrt] = useState('');
  const [suche, setSuche] = useState('');
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [schreiben, setSchreiben] = useState<{ an?: string } | null>(null);
  const [amServer, setAmServer] = useState(false);
  /* .489: zählt hoch, wenn „Postfächer verwalten“ geklickt wird; öffnet den Dialog. */
  const [verwaltenAuf, setVerwaltenAuf] = useState(0);
  const [meldung, setMeldung] = useState<string | null>(null);
  const suchfeld = useRef<HTMLInputElement | null>(null);

  const darfSenden = darf(ich, 'nachricht.senden');

  async function laden() {
    try {
      const r = await api.postfachVorgaenge({ ansicht, tag, konto, ort, suche: suche.trim() });
      setD(r); setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { const t = setTimeout(laden, suche ? 250 : 0); return () => clearTimeout(t); },
            [ansicht, tag, konto, ort, suche]);
  useFrisch(laden);

  const zeilen: any[] = d?.zeilen ?? [];
  const tagfarbe = useMemo(() => new Map<string, string>((d?.tags ?? []).map((t: any) => [t.name, t.farbe])), [d]);

  /* DIE TASTATUR. Nie, während jemand in ein Feld schreibt. */
  useEffect(() => {
    function taste(e: KeyboardEvent) {
      const ziel = e.target as HTMLElement | null;
      if (ziel && (ziel.closest('input, textarea, select, [contenteditable="true"]') || ziel.closest('.dialoggrund'))) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const i = zeilen.findIndex((z) => z.faden === offen);
      if (e.key === 'j' || e.key === 'ArrowDown') {
        const n = zeilen[Math.min(zeilen.length - 1, i + 1)]; if (n) { setOffen(n.faden); e.preventDefault(); }
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        const n = zeilen[Math.max(0, i - 1)]; if (n) { setOffen(n.faden); e.preventDefault(); }
      } else if (e.key === '/') { suchfeld.current?.focus(); e.preventDefault(); }
      else if (e.key === 'c' && darfSenden) { setSchreiben({}); e.preventDefault(); }
      else if (offen && (e.key === 'r' || e.key === 'n' || e.key === 'e')) {
        window.dispatchEvent(new CustomEvent('postfach-taste', { detail: e.key }));
        e.preventDefault();
      }
    }
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [zeilen, offen, darfSenden]);

  const aktiv = ANSICHTEN.find((a) => a.schluessel === ansicht);

  return (
    <>
      <Kopf
        titel="Postfach"
        weg={amServer ? 'Die Ordner am Mailserver'
          : d ? `${ort ? ((d.baum ?? []).find((b: any) => `${b.konto_id}|${b.roh}` === ort)?.name ?? 'Ordner') : (aktiv?.name ?? '')}${tag ? `, Tag „${tag}"` : ''}${konto ? `, ${konto}` : ''}: ${zeilen.length}`
            + `${d.gekuerzt ? '+' : ''} ${zeilen.length === 1 ? 'Vorgang' : 'Vorgänge'}` : 'Wird geladen.'}
        rechts={
          <div className="reihe">
            {!amServer && (
              <input ref={suchfeld} type="search" autoComplete="off" value={suche}
                     placeholder="Suchen: Betreff, Absender, Text, Tag, Straße, KLS  ( / )"
                     style={{ minWidth: 280 }} onChange={(e) => setSuche(e.target.value)} />
            )}
            {darfSenden && (
              <button type="button" className="knopf haupt" onClick={() => setSchreiben({})}>
                Neue Mail
              </button>
            )}
          </div>
        }
      />

      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {meldung && <div className="hinweis erledigt" onClick={() => setMeldung(null)}>{meldung}</div>}

        <div className={'mailprogramm postarbeit' + (offen && !amServer ? ' liest' : '')}>

          {/* ------------------------------------------------ die Leiste */}
          <nav className="mailordner">
            {ANSICHTEN.map((o) => (
              <button key={o.schluessel} type="button"
                      className={'ordnerknopf' + (!amServer && ansicht === o.schluessel && !tag && !konto && !ort ? ' aktiv' : '')}
                      title={o.hilfe}
                      onClick={() => { setAnsicht(o.schluessel); setTag(''); setKonto(''); setOrt(''); setAmServer(false); setOffen(null); }}>
                <span className="zeichen" aria-hidden="true">{o.zeichen}</span>
                <span className="wachsen">{o.name}</span>
                {(d?.zahl?.[o.schluessel] ?? 0) > 0 && o.schluessel !== 'alle' && o.schluessel !== 'erledigt'
                  && o.schluessel !== 'gesendet' && <span className="zahl">{d.zahl[o.schluessel]}</span>}
              </button>
            ))}
            <div className="leistenkopf">Tags</div>
            {(d?.tags ?? []).map((t: any) => (
              <button key={t.name} type="button"
                      className={'ordnerknopf tagknopf' + (!amServer && tag === t.name ? ' aktiv' : '')}
                      onClick={() => { setTag(tag === t.name ? '' : t.name); setAmServer(false); setOffen(null);
                                       if (tag !== t.name) setAnsicht('alle'); }}>
                <span className="tagpunkt" style={{ background: t.farbe }} aria-hidden="true" />
                <span className="wachsen">{t.name}</span>
                {t.offen > 0 && <span className="zahl">{t.offen}</span>}
              </button>
            ))}
            {/* DIE ORDNER WIE AM SERVER (.287). Solange der Abgleich noch
                nichts gelesen hat, stehen die Postfächer nach ihrem Namen da. */}
            {(d?.baum ?? []).length > 0 ? (
              <>
                <div className="leistenkopf">Postfächer</div>
                {[...new Set((d.baum as any[]).map((b) => b.konto))].map((kname) => (
                  <div key={kname} className="ordnerbaum">
                    <div className="leistenkopf">{kname}</div>
                    {(d.baum as any[]).filter((b) => b.konto === kname).map((b) => {
                      const schl = `${b.konto_id}|${b.roh}`;
                      return (
                        <button key={schl} type="button"
                                className={'ordnerknopf baumknopf' + (!amServer && ort === schl ? ' aktiv' : '')}
                                style={{ paddingLeft: 10 + 14 * (b.ebene ?? 0) }}
                                title={`${b.name}: ${b.anzahl ?? 0} Mails am Server, ${b.vorgaenge} Vorgänge hier`}
                                onClick={() => { setOrt(ort === schl ? '' : schl); setAnsicht('alle'); setTag(''); setKonto('');
                                                 setAmServer(false); setOffen(null); }}>
                          <span className="zeichen" aria-hidden="true">{b.rolle === 'posteingang' ? '⬇' : b.rolle === 'gesendet' ? '⬆'
                            : b.rolle === 'papierkorb' ? '🗑' : '📁'}</span>
                          <span className="wachsen">{b.kurz}</span>
                          {b.ungelesen > 0 ? <span className="zahl">{b.ungelesen}</span>
                            : b.vorgaenge > 0 ? <span className="klein leise">{b.vorgaenge}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </>
            ) : (
              <>
                {(d?.konten ?? []).length > 0 && <div className="leistenkopf">Postfächer</div>}
                {(d?.konten ?? []).map((k: string) => (
                  <button key={k} type="button"
                          className={'ordnerknopf' + (!amServer && konto === k ? ' aktiv' : '')}
                          onClick={() => { setKonto(konto === k ? '' : k); setOrt(''); setAmServer(false); setOffen(null); }}>
                    <span className="zeichen" aria-hidden="true">📥</span>
                    <span className="wachsen">{k}</span>
                  </button>
                ))}
              </>
            )}
            {/* .490: „Ordner am Server“ und „Postfächer verwalten“ sind rein administrativ. */}
            {darf(ich, 'postfach.konten' as any) && (
            <button type="button" className={'ordnerknopf' + (amServer ? ' aktiv' : '')}
                    title="Die Ordner direkt am Mailserver, wie in Outlook: verschieben, löschen, an einen Auftrag ziehen."
                    onClick={() => { setAmServer(true); setOffen(null); }}>
              <span className="zeichen" aria-hidden="true">⚙</span>
              <span className="wachsen">Ordner am Server</span>
            </button>
            )}
            {darf(ich, 'postfach.konten' as any) && (
              <button type="button" className="ordnerknopf"
                      onClick={() => { setVerwaltenAuf((n) => n + 1); setAmServer(true); setOffen(null); }}>
                <span className="zeichen" aria-hidden="true">➕</span>
                <span className="wachsen">Postfächer verwalten</span>
              </button>
            )}
            <p className="klein leise tastenhilfe">
              j/k blättern, r antworten, n Notiz, e erledigt, c neue Mail, / suchen
            </p>
          </nav>

          {amServer && darf(ich, 'postfach.konten' as any) ? (
            <div className="postserver"><Mailfach key={verwaltenAuf} ich={ich} oeffne={oeffne} verwaltenAuf={verwaltenAuf > 0} /></div>
          ) : (
            <>
              {/* ------------------------------------------------- die Liste */}
              <div className="mailliste">
                {!d && !fehler && <p className="klein leise" style={{ padding: 12 }}>Wird geladen.</p>}
                {d && zeilen.length === 0 && (
                  <div className="postleer">
                    <div className="grosszeichen" aria-hidden="true">✓</div>
                    <b>{suche ? 'Nichts gefunden.' : 'Hier liegt nichts.'}</b>
                    <span className="klein leise">{aktiv?.hilfe}</span>
                  </div>
                )}
                {zeilen.map((z) => (
                  <button key={z.faden} type="button"
                          className={'mailzeile postzeile' + (offen === z.faden ? ' aktiv' : '')
                                     + (z.ungelesen > 0 ? ' ungelesen' : '')}
                          onClick={() => setOffen(z.faden)}>
                    <Rund name={z.letzte_richtung === 'aus' ? ((z.empfaenger ?? [])[0] ?? '?') : (z.absender_name || z.absender || '?')} />
                    <span className="postzeileleib">
                      <span className="oben">
                        <b className="wachsen">{wer(z)}{z.anzahl > 1 && <span className="leise"> ({z.anzahl})</span>}</b>
                        {z.ungelesen > 0 && <span className="ungelesenpunkt" title="ungelesen" />}
                        <span className="klein leise nichtumbrechen">{wann(z.zuletzt)}</span>
                      </span>
                      <span className="betreff">{z.betreff || <span className="leise">ohne Betreff</span>}</span>
                      <span className="auszug klein leise">{z.auszug}</span>
                      <span className="unten klein">
                        {z.letzte_richtung === 'aus' && <span className="postmarke raus">↑ gesendet</span>}
                        {z.offen_ein === 0 && z.letzte_richtung !== 'aus' && <span className="postmarke fertig">✓ erledigt</span>}
                        {z.auftrag_id
                          ? <span className="postmarke auftrag">📍 {z.strasse ? `${z.strasse} ${z.hausnummer}` : (z.kls_id ?? 'Auftrag')}</span>
                          : (z.offen_ein > 0 && <span className="postmarke ohne">ohne Auftrag</span>)}
                        {(z.tags ?? []).map((t: string) => <Tagchip key={t} name={t} farbe={tagfarbe.get(t)} />)}
                        {z.notizen > 0 && <span className="postmarke" title="Notizen">💬 {z.notizen}</span>}
                        {z.anhaenge > 0 && (
                          <span className="postmarke" title={z.anhaenge === 1 ? '1 Anhang' : `${z.anhaenge} Anhänge`}>
                            <Klammer /> {z.anhaenge}
                          </span>
                        )}
                        {z.wiedervorlage && <span className="postmarke" title="Wiedervorlage">⏰ {datum(z.wiedervorlage)}</span>}
                        {z.zustaendig_name && <Rund name={z.zustaendig_name} klein />}
                      </span>
                    </span>
                  </button>
                ))}
              </div>

              {/* ------------------------------------------------ der Vorgang */}
              <div className="maillese">
                {offen ? (
                  <Vorgang faden={offen} ich={ich} tagfarbe={tagfarbe}
                           zurueck={() => setOffen(null)} fertig={laden} oeffne={oeffne}
                           neueMail={(an) => setSchreiben({ an })} />
                ) : (
                  <div className="leerelese klein leise">
                    Links einen Vorgang anklicken, oder mit j und k durch die Liste gehen.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {schreiben && (
        <MailSchreiben an={schreiben.an} zu={() => setSchreiben(null)}
                       fertig={(faden, satz) => { setSchreiben(null); setMeldung(satz); setAnsicht('gesendet');
                                                  setTag(''); setKonto(''); setOffen(faden); laden(); }} />
      )}
    </>
  );
}

/**
 * Ein Vorgang: Zeitachse, Antwort- und Notizfeld, und rechts was daran hängt.
 */
function Vorgang(
  { faden, ich, tagfarbe, zurueck, fertig, oeffne, neueMail }: {
    faden: string; ich: Anmelder; tagfarbe: Map<string, string>;
    zurueck: () => void; fertig: () => void;
    oeffne: (auftragId: string) => void; neueMail: (an: string) => void;
  }
) {
  /* DER HAKEN STEHT AM ANFANG DES BAUTEILS und läuft bei jedem Durchgang.
     Einer hinter einer bedingten Rückgabe ist auf dem Telefon ein weisser
     Bildschirm (React-Fehler 310). */
  const { fenster: dateifenster, zeige: zeigeDatei } = useDateifenster();
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [filter, setFilter] = useState<'alles' | 'mail' | 'notiz'>('alles');
  const [modus, setModus] = useState<'antwort' | 'notiz' | null>(null);
  const [auf, setAuf] = useState<Set<string>>(new Set());
  const [server, setServer] = useState<any>(null);
  const darfZuordnen = darf(ich, 'postfach.zuordnen');
  const darfSenden = darf(ich, 'nachricht.senden');
  const darfNotiz = darf(ich, 'postfach.notiz');

  async function laden() {
    try { setD(await api.postfachVorgang(faden)); setFehler(null); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  useFrisch(laden);
  /* DIE ANHÄNGE VOM SERVER (.287) kommen getrennt: der Mailserver ist
     langsamer als die Datenbank, und der Vorgang soll nicht auf ihn warten. */
  async function serverLaden() {
    try { setServer(await api.postfachServer(faden)); } catch { setServer({ mails: [], fehler: ['Der Mailserver war nicht zu erreichen.'] }); }
  }
  useEffect(() => { setD(null); setServer(null); setModus(null); setMeldung(null); setAuf(new Set()); laden(); serverLaden(); }, [faden]);

  async function holen(x: any) {
    setLaeuft(true); setFehler(null);
    try {
      await api.mailfachAnAuftrag(x.konto_id, { ordner: x.ordner, uid: x.uid, auftrag_id: d?.auftrag?.id ?? null });
      setMeldung('Die Anhänge sind in der Plattform' + (d?.auftrag ? ' und hängen am Auftrag.' : '.'));
      await laden(); await serverLaden(); fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  /** Was nur am Server liegt: ohne die Anhänge, die schon hier sind. */
  function nurAmServer(mailId: string): { x: any; liste: any[] } | null {
    const x = (server?.mails ?? []).find((m: any) => m.mail_id === mailId);
    if (!x) return null;
    const hier = new Set((d?.anhaenge ?? []).filter((a: any) => a.mail_id === mailId).map((a: any) => String(a.dateiname).toLowerCase()));
    const liste = (x.anhaenge ?? []).filter((a: any) => !hier.has(String(a.dateiname).toLowerCase()));
    return { x, liste };
  }

  async function aendern(b: Record<string, unknown>, satz?: string) {
    setLaeuft(true); setFehler(null); setMeldung(null);
    try { await api.postfachVorgangAendern(faden, b); if (satz) setMeldung(satz); await laden(); fertig(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  useEffect(() => {
    function t(e: Event) {
      const k = (e as CustomEvent).detail;
      if (k === 'r' && darfSenden) setModus('antwort');
      if (k === 'n' && darfNotiz) setModus('notiz');
      if (k === 'e' && darfZuordnen && d) aendern({ erledigt: !d.kopf.erledigt }, d.kopf.erledigt ? 'Wieder offen.' : 'Erledigt.');
    }
    window.addEventListener('postfach-taste', t);
    return () => window.removeEventListener('postfach-taste', t);
  }, [d, darfSenden, darfNotiz, darfZuordnen]);

  const k = d?.kopf;
  const zeitachse: any[] = (d?.zeitachse ?? []).filter((x: any) =>
    filter === 'alles' || x.art === filter);
  const letzteMail = [...(d?.zeitachse ?? [])].reverse().find((x: any) => x.art === 'mail');
  const sichtbare: Kacheldatei[] = (d?.anhaenge ?? []).filter((x: any) => x.datei_bezug_id).map(kacheldatei);

  return (
    <div className="lesespalte postvorgang">
      <button type="button" className="knopf zurueckzurliste" onClick={zurueck}>&larr; Liste</button>
      <Fehlerkasten lage={fehler} />
      {!d && !fehler && <p className="klein leise">Wird geladen.</p>}

      {k && (
        <div className="vorgangraster">
            <div className="lesekopf">
            <h2>{k.betreff || <span className="leise">ohne Betreff</span>}</h2>
            <div className="reihe postaktionen">
              {darfSenden && <button type="button" className="knopf haupt" onClick={() => setModus('antwort')}>↩ Antworten</button>}
              {darfNotiz && <button type="button" className="knopf" onClick={() => setModus('notiz')}>💬 Notiz</button>}
              {darfZuordnen && (
                <button type="button" className={'knopf' + (k.erledigt ? '' : ' erledigtknopf')} disabled={laeuft}
                        onClick={() => aendern({ erledigt: !k.erledigt }, k.erledigt ? 'Wieder offen.' : 'Erledigt.')}>
                  {k.erledigt ? 'Wieder aufmachen' : '✓ Erledigt'}
                </button>
              )}
              {d.auftrag && (
                <button type="button" className="knopf" onClick={() => oeffne(d.auftrag.id)}>Akte öffnen &rarr;</button>
              )}
            </div>
            {meldung && <div className="hinweis erledigt" style={{ marginTop: 8 }}>{meldung}</div>}
          </div>

          {/* --------------------------------------------- was daran hängt */}
          <aside className="vorgangseite vorgangleiste">
            <section>
              <h4>Auftrag</h4>
              {d.auftrag ? (
                <div className="postauftrag">
                  <button type="button" className="kartenweg" onClick={() => oeffne(d.auftrag.id)}>
                    {d.auftrag.strasse} {d.auftrag.hausnummer}{d.auftrag.hausnummer_zusatz ?? ''} &rarr;
                  </button>
                  <div className="klein leise">{d.auftrag.plz} {d.auftrag.ort_text}{d.auftrag.gemeinde ? `, ${d.auftrag.gemeinde}` : ''}</div>
                  <div className="klein">KLS {d.auftrag.kls_id ?? '-'}{d.auftrag.sm_nummer ? `, SM ${d.auftrag.sm_nummer}` : ''}</div>
                  {k.zuordnung_wie && <div className="klein leise">{k.zuordnung_wie}</div>}
                </div>
              ) : <div className="klein leise">Noch keinem Auftrag zugeordnet.</div>}
              {darfZuordnen && (
                <ZuordnenKnopf laeuft={laeuft} titel={d.auftrag ? 'Anderer Auftrag' : 'Auftrag zuordnen'} ohneAufheben={!d.auftrag}
                               tu={(id) => aendern({ auftrag_id: id }, id ? 'Zugeordnet, der ganze Vorgang.' : 'Zuordnung aufgehoben.')} />
              )}
            </section>

            <section>
              <h4>Zuständig</h4>
              <Zustaendig kopf={k} ich={ich} darf={darfZuordnen} laeuft={laeuft}
                          setze={(id) => aendern({ zustaendig_id: id })} />
            </section>

            <section>
              <h4>Tags</h4>
              <Tags kopf={k} tagfarbe={tagfarbe} darf={darfZuordnen || darfNotiz} laeuft={laeuft}
                    setze={(tags) => aendern({ tags })} />
            </section>

            <section>
              <h4>Wiedervorlage</h4>
              <Wiedervorlage wert={k.wiedervorlage} darf={darfZuordnen || darfNotiz} laeuft={laeuft}
                             setze={(w) => aendern({ wiedervorlage: w })} />
            </section>

            {(k.beteiligte ?? []).length > 0 && (
              <section>
                <h4>Beteiligte</h4>
                {(k.beteiligte ?? []).map((b: any) => (
                  <div key={b.adresse} className="beteiligt">
                    <Rund name={b.name || b.adresse} klein />
                    <span className="wachsen">
                      {b.name && <div className="klein"><b>{b.name}</b></div>}
                      <div className="klein leise">{b.adresse}</div>
                    </span>
                    {darfSenden && (
                      <button type="button" className="knopf klein" title="Neue Mail an diese Adresse"
                              onClick={() => neueMail(b.adresse)}>schreiben</button>
                    )}
                  </div>
                ))}
              </section>
            )}

            {(d.anhaenge ?? []).length > 0 && (
              <section>
                <h4><Klammer /> {d.anhaenge.length === 1 ? '1 Anhang' : `${d.anhaenge.length} Anhänge`}</h4>
                {d.anhaenge.map((x: any) => (
                  <Anhang key={x.id} x={x} laeuft={laeuft} darfZuordnen={darfZuordnen} oeffne={oeffne}
                          zeige={(kd) => zeigeDatei(kd, sichtbare)}
                          tu={async (auftragId) => {
                            setLaeuft(true);
                            try { await api.eingangEinsortieren(x.id, auftragId); setMeldung('Der Anhang hängt jetzt am Auftrag.'); await laden(); }
                            catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
                          }} />
                ))}
              </section>
            )}
            {(server?.mails ?? []).some((m: any) => (nurAmServer(m.mail_id)?.liste.length ?? 0) > 0) && (
              <section>
                <h4><Klammer /> Am Mailserver</h4>
                {(server.mails as any[]).map((m: any) => {
                  const sv = nurAmServer(m.mail_id);
                  if (!sv || !sv.liste.length) return null;
                  return (
                    <div key={m.mail_id} className="serveranhaenge">
                      {sv.liste.map((a: any) => (
                        <a key={a.nummer} className="postmarke" target="_blank" rel="noreferrer"
                           href={api.mailfachAnhangAdresse(m.konto_id, m.ordner, m.uid, a.nummer)}>{a.dateiname}</a>
                      ))}
                      {darfZuordnen && (
                        <button type="button" className="knopf klein" disabled={laeuft} onClick={() => holen(m)}>
                          {d.auftrag ? 'An den Auftrag hängen' : 'In die Plattform holen'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </section>
            )}
            {server === null && <p className="klein leise">Anhänge am Mailserver werden gesucht …</p>}
            {(server?.fehler ?? []).length > 0 && <p className="klein leise">Mailserver: {server.fehler.join(', ')}</p>}
            {(k.postfaecher ?? []).length > 0 && (
              <p className="klein leise">Postfach: {k.postfaecher.join(', ')}</p>
            )}
          </aside>
          <div className="vorgangmitte">
            <div className="reiter postfilter">
              {(['alles', 'mail', 'notiz'] as const).map((f) => (
                <button key={f} type="button" aria-current={filter === f} onClick={() => setFilter(f)}>
                  {f === 'alles' ? 'Alles' : f === 'mail' ? 'Mails' : 'Notizen'}
                  {f !== 'alles' && <span className="leise"> {(d.zeitachse ?? []).filter((x: any) => x.art === f).length}</span>}
                </button>
              ))}
            </div>

            <ol className="zeitachse">
              {zeitachse.map((x: any) => {
                if (x.art === 'ereignis') {
                  return (
                    <li key={'e' + x.id} className="zeitpunkt ereignis">
                      <span className="zeitsymbol">•</span>
                      <span className="klein leise">{x.text}{x.verfasser_name ? `, ${x.verfasser_name}` : ''}, <span title={zeitpunkt(x.zeit)}>{vorWann(x.zeit)}</span></span>
                    </li>
                  );
                }
                if (x.art === 'notiz') {
                  const n = x;
                  return (
                    <li key={'n' + x.id} className="zeitpunkt notiz notizzeile">
                      <span className="zeitsymbol">💬</span>
                      <div className="zeitkarte">
                        <div className="zeitkopf">
                          <b>{x.verfasser_name}</b> <span className="leise">hat notiert</span>
                          <span className="wachsen" />
                          <span className="klein leise" title={zeitpunkt(x.zeit)}>{vorWann(x.zeit)}</span>
                        </div>
                        <div className="notiztext"><Nennungen text={x.text} /></div>
                        {x.bearbeitet_am && <div className="klein leise">berichtigt von {x.bearbeitet_name ?? 'jemandem'}</div>}
                        {/* EIN KNOPF, DER AUF EIN 403 FÜHRT, IST SCHLIMMER ALS KEINER. */}
                        {darfNotiz && (n.verfasser_id === ich.benutzerId || darf(ich, 'postfach.zuordnen')) && (
                          <button type="button" className="knopf klein" disabled={laeuft}
                                  onClick={async () => {
                                    try { await api.postfachNotizAendern(x.id, { weg: true }); await laden(); fertig(); }
                                    catch (f) { setFehler(ausFehler(f)); }
                                  }}>wegnehmen</button>
                        )}
                      </div>
                    </li>
                  );
                }
                const raus = x.richtung === 'aus';
                const istAuf = auf.has(x.id) || x.id === letzteMail?.id;
                const anhaengeHier = (d.anhaenge ?? []).filter((a: any) => a.mail_id === x.id);
                return (
                  <li key={'m' + x.id} className={'zeitpunkt fadenzeile ' + (raus ? 'raus' : 'rein')}>
                    <span className="zeitsymbol">{raus ? '↑' : '↓'}</span>
                    <div className="zeitkarte">
                      <button type="button" className="zeitkopf knopflos"
                              onClick={() => setAuf((s) => { const n = new Set(s); n.has(x.id) ? n.delete(x.id) : n.add(x.id); return n; })}>
                        <Rund name={raus ? (x.verfasser ?? 'MDS') : (x.absender_name || x.absender || '?')} klein />
                        <b>{raus ? (x.verfasser ?? 'MDS') : (x.absender_name || x.absender)}</b>
                        <span className="leise klein">
                          {raus ? ` an ${(x.empfaenger ?? []).join(', ')}` : ` <${(x.absender ?? '').replace(/.*<|>.*/g, '')}>`}
                          {(x.kopie ?? []).length ? `, Cc ${(x.kopie ?? []).join(', ')}` : ''}
                        </span>
                        <span className="wachsen" />
                        {x.ordner_name && <span className="postmarke" title={`Am Server: ${x.konto_name ?? ''} / ${x.ordner_name}`}>📁 {x.ordner_name}</span>}
                        {x.erfolgreich === false && <span className="postmarke ohne">nicht hinausgegangen</span>}
                        <span className="klein leise" title={zeitpunkt(x.zeit)}>{vorWann(x.zeit)}</span>
                      </button>
                      {istAuf ? (
                        <>
                          {x.betreff && x.betreff !== k.betreff && <div className="klein"><b>{x.betreff}</b></div>}
                          <pre className="mailtext">{x.koerper_text || '(ohne Text)'}</pre>
                          {anhaengeHier.length > 0 && (
                            <div className="reihe" style={{ flexWrap: 'wrap', gap: 6 }}>
                              {anhaengeHier.map((a: any) => (
                                <Dateikachel key={a.id} datei={kacheldatei(a)}
                                             oeffne={a.datei_bezug_id ? () => zeigeDatei(kacheldatei(a), sichtbare) : undefined} />
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="klein leise zeitauszug">{(x.koerper_text ?? '').replace(/\s+/g, ' ').slice(0, 160)}</div>
                      )}
                      {(() => {
                        const sv = nurAmServer(x.id);
                        if (!sv || !sv.liste.length) return null;
                        return (
                          <div className="serveranhaenge">
                            <span className="klein leise"><Klammer /> am Server:</span>
                            {sv.liste.map((a: any) => (
                              <a key={a.nummer} className="postmarke" target="_blank" rel="noreferrer"
                                 href={api.mailfachAnhangAdresse(sv.x.konto_id, sv.x.ordner, sv.x.uid, a.nummer)}
                                 title={`${a.art}, ${Math.max(1, Math.round((a.groesse ?? 0) / 1024))} KB`}>{a.dateiname}</a>
                            ))}
                            {darfZuordnen && (
                              <button type="button" className="knopf klein" disabled={laeuft} onClick={() => holen(sv.x)}>
                                {d.auftrag ? 'An den Auftrag hängen' : 'In die Plattform holen'}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </li>
                );
              })}
            </ol>

            {modus && (
              <Schreibfeld modus={modus} setModus={setModus} kopf={k} ich={ich}
                           darfSenden={darfSenden} darfNotiz={darfNotiz}
                           fertig={async (satz) => { setModus(null); setMeldung(satz); await laden(); fertig(); }} />
            )}
            {!modus && (darfSenden || darfNotiz) && (
              <button type="button" className="schreibanstoss" onClick={() => setModus(darfSenden ? 'antwort' : 'notiz')}>
                {darfSenden ? `Antwort an ${k.antwort_an} schreiben, oder eine Notiz …` : 'Eine Notiz schreiben …'}
              </button>
            )}
          </div>

        </div>
      )}
      {dateifenster}
    </div>
  );
}

/** Ein @Name in einer Notiz ist hervorgehoben. */
function Nennungen({ text }: { text: string }) {
  const teile = String(text ?? '').split(/(@[0-9A-Za-zÄÖÜäöüß][0-9A-Za-zÄÖÜäöüß.-]*(?:\s[A-ZÄÖÜ][a-zäöüß]+)?)/g);
  return <>{teile.map((t, i) => t.startsWith('@') ? <span key={i} className="nennung">{t}</span> : <span key={i}>{t}</span>)}</>;
}

/**
 * Das Feld unter der Zeitachse: Antwort oder Notiz, mit einem Klick
 * umgeschaltet - wie im Bild: was hinausgeht und was im Haus bleibt, sind
 * zwei Reiter desselben Felds und nicht zwei Karten an verschiedenen Stellen.
 */
function Schreibfeld(
  { modus, setModus, kopf, ich, darfSenden, darfNotiz, fertig }: {
    modus: 'antwort' | 'notiz'; setModus: (m: 'antwort' | 'notiz' | null) => void;
    kopf: any; ich: Anmelder; darfSenden: boolean; darfNotiz: boolean;
    fertig: (satz: string) => void;
  }
) {
  const [an, setAn] = useState(kopf.antwort_an ? kopf.antwort_an + ', ' : '');
  const [cc, setCc] = useState('');
  const [mitCc, setMitCc] = useState(false);
  const [betreff, setBetreff] = useState(/^re:/i.test(kopf.betreff ?? '') ? kopf.betreff : `Re: ${kopf.betreff ?? ''}`);
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [befund, setBefund] = useState<any>(null);
  const [leute, setLeute] = useState<any[] | null>(null);
  const [wahl, setWahl] = useState<{ auf: boolean; ab: number }>({ auf: false, ab: 0 });
  const feld = useRef<HTMLTextAreaElement | null>(null);
  const vorlagen = useVorlagen();

  useEffect(() => { feld.current?.focus(); }, [modus]);
  /* DIE NENNBAREN KOMMEN EINMAL UND VOM SERVER. */
  useEffect(() => {
    if (!darfNotiz || leute) return;
    api.postfachNennbar().then((r) => setLeute(r.leute ?? [])).catch(() => setLeute([]));
  }, [darfNotiz]);

  function allen() {
    const alle = (kopf.beteiligte ?? []).map((b: any) => b.adresse).filter((a: string) => !adressen(an).includes(a));
    setCc(alle.join(', ')); setMitCc(true);
  }

  async function los() {
    if (!text.trim()) return;
    setLaeuft(true); setFehler(null); setBefund(null);
    try {
      if (modus === 'antwort') {
        const r = await api.postfachAntwort(kopf.antwort_auf, { an: adressen(an), cc: adressen(cc), betreff, text });
        setText('');
        fertig(r.gesendet ? (r.umgeleitet ? 'Gesendet, im Testmodus an die Testadresse.' : 'Die Antwort ist unterwegs.')
                          : `Nicht hinausgegangen: ${r.grund ?? 'unbekannt'}`);
      } else {
        const r = await api.postfachNotiz(kopf.letzte_id, text);
        setText('');
        if ((r.unbekannt ?? []).length || (r.mehrdeutig ?? []).length) { setBefund(r); setLaeuft(false); return; }
        fertig((r.nennungen ?? []).length ? `Notiz gespeichert, gemeldet an ${r.nennungen.map((x: any) => x.name).join(', ')}.` : 'Notiz gespeichert.');
      }
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  /* DIE AUSWAHL GEHT AUF, WENN JEMAND EIN @ TIPPT. */
  function tippen(neu: string) {
    setText(neu);
    if (modus !== 'notiz') return;
    const bis = feld.current?.selectionStart ?? neu.length;
    const m = /(^|[^0-9A-Za-zÄÖÜäöüß._-])@([0-9A-Za-zÄÖÜäöüß]*)$/.exec(neu.slice(0, bis));
    setWahl(m ? { auf: true, ab: bis - (m[2]?.length ?? 0) } : { auf: false, ab: 0 });
  }
  function nimm(name: string) {
    const bis = feld.current?.selectionStart ?? text.length;
    setText(text.slice(0, wahl.ab) + name + ' ' + text.slice(bis));
    setWahl({ auf: false, ab: 0 });
    feld.current?.focus();
  }
  const suchbegriff = wahl.auf ? text.slice(wahl.ab, feld.current?.selectionStart ?? text.length).toLowerCase() : '';
  const treffer = (leute ?? []).filter((l) => !suchbegriff || l.name.toLowerCase().includes(suchbegriff)).slice(0, 8);

  return (
    <div className={'schreibfeld ' + modus}
         onKeyDown={(e) => {
           if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); los(); }
           if (e.key === 'Escape') setModus(null);
         }}>
      <div className="schreibkopf">
        <div className="reiter">
          {darfSenden && <button type="button" aria-current={modus === 'antwort'} onClick={() => setModus('antwort')}>↩ Antwort</button>}
          {darfNotiz && <button type="button" aria-current={modus === 'notiz'} onClick={() => setModus('notiz')}>💬 Notiz (intern)</button>}
        </div>
        <button type="button" className="knopf klein" onClick={() => setModus(null)}>schließen</button>
      </div>
      <Fehlerkasten lage={fehler} />
      {modus === 'antwort' && (
        <>
          <label className="mailformzeile"><span>An</span>
            <Adressfeld name="an" wert={an} setze={setAn} />
            <button type="button" className="knopf klein" onClick={allen} title="Alle Beteiligten in Kopie">Allen</button>
            {!mitCc && <button type="button" className="knopf klein" onClick={() => setMitCc(true)}>Cc</button>}
          </label>
          {mitCc && <label className="mailformzeile"><span>Cc</span><Adressfeld name="cc" wert={cc} setze={setCc} /></label>}
          <label className="mailformzeile"><span>Betreff</span>
            <input type="text" value={betreff} onChange={(e) => setBetreff(e.target.value)} />
          </label>
        </>
      )}
      <div className="notizfeld">
        <textarea ref={feld} className="feld" rows={modus === 'antwort' ? 8 : 3} value={text}
                  placeholder={modus === 'antwort' ? 'Antwort schreiben …' : 'Notiz für das Haus. Mit @ jemanden nennen, er bekommt eine Meldung an der Glocke.'}
                  onChange={(e) => tippen(e.target.value)} />
        {wahl.auf && treffer.length > 0 && (
          <div className="nennliste">
            {treffer.map((l) => (
              <button key={l.id} type="button" className="knopf klein" onClick={() => nimm(l.name)}>{l.name}</button>
            ))}
          </div>
        )}
      </div>
      {/* WAS NICHT ANKAM, STEHT DA. */}
      {befund && (befund.nennungen?.length > 0 || befund.unbekannt?.length > 0 || befund.mehrdeutig?.length > 0) && (
        <div className="hinweis klein">
          {befund.nennungen?.length > 0 && <div>Gemeldet an: {befund.nennungen.map((x: any) => x.name).join(', ')}.</div>}
          {befund.unbekannt?.length > 0 && <div>Kein Zugang heißt so: {befund.unbekannt.join(', ')}. Diese Namen melden niemanden.</div>}
          {befund.mehrdeutig?.length > 0 && <div>Mehrdeutig: {befund.mehrdeutig.join(', ')}. Bitte den ganzen Namen schreiben.</div>}
        </div>
      )}
      <div className="reihe">
        <button type="button" className="knopf haupt" disabled={laeuft || !text.trim() || (modus === 'antwort' && !adressen(an).length)}
                onClick={los}>
          {laeuft ? 'Einen Moment' : modus === 'antwort' ? 'Senden' : 'Notiz speichern'}
        </button>
        {modus === 'antwort' && vorlagen.length > 0 && (
          <select value="" onChange={(e) => {
            const v = vorlagen.find((x) => x.wert === e.target.value);
            if (v) setText((t) => (t ? t + '\n\n' : '') + v.text);
          }}>
            <option value="">Vorlage einfügen …</option>
            {vorlagen.map((v) => <option key={v.wert} value={v.wert}>{v.name}</option>)}
          </select>
        )}
        <span className="klein leise">Strg + Enter {modus === 'antwort' ? 'sendet' : 'speichert'}, Esc schließt
          {modus === 'antwort' ? ', im Testmodus an die Testadresse' : `, als ${ich.name}`}</span>
      </div>
    </div>
  );
}

function Zustaendig({ kopf, ich, darf: darfEs, laeuft, setze }: {
  kopf: any; ich: Anmelder; darf: boolean; laeuft: boolean; setze: (id: string | null) => void;
}) {
  const [leute, setLeute] = useState<any[]>([]);
  useEffect(() => { api.postfachNennbar().then((r) => setLeute(r.leute ?? [])).catch(() => setLeute([])); }, []);
  if (!darfEs) return <div className="klein">{kopf.zustaendig_name ?? <span className="leise">niemand</span>}</div>;
  return (
    <div className="reihe" style={{ gap: 6 }}>
      {kopf.zustaendig_name && <Rund name={kopf.zustaendig_name} klein />}
      <select value={kopf.zustaendig_id ?? ''} disabled={laeuft} onChange={(e) => setze(e.target.value || null)}>
        <option value="">niemand</option>
        {leute.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
      {kopf.zustaendig_id !== ich.benutzerId && (
        <button type="button" className="knopf klein" disabled={laeuft} onClick={() => setze(ich.benutzerId ?? null)}>Ich</button>
      )}
    </div>
  );
}

function Tags({ kopf, tagfarbe, darf: darfEs, laeuft, setze }: {
  kopf: any; tagfarbe: Map<string, string>; darf: boolean; laeuft: boolean; setze: (t: string[]) => void;
}) {
  const [neu, setNeu] = useState('');
  const tags: string[] = kopf.tags ?? [];
  const vorschlag = [...tagfarbe.keys()].filter((t) => !tags.includes(t));
  function dazu(t: string) {
    const s = t.trim();
    if (!s || tags.includes(s)) { setNeu(''); return; }
    setze([...tags, s]); setNeu('');
  }
  return (
    <div className="posttags">
      {tags.length === 0 && <span className="klein leise">noch keine</span>}
      {tags.map((t) => <Tagchip key={t} name={t} farbe={tagfarbe.get(t)}
                                weg={darfEs && !laeuft ? () => setze(tags.filter((x) => x !== t)) : undefined} />)}
      {darfEs && (
        <>
          <input type="text" list="posttagliste" value={neu} placeholder="+ Tag" className="tageingabe"
                 onChange={(e) => setNeu(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); dazu(neu); } }}
                 onBlur={() => neu.trim() && dazu(neu)} />
          <datalist id="posttagliste">{vorschlag.map((t) => <option key={t} value={t} />)}</datalist>
          <div className="tagvorschlag">
            {vorschlag.slice(0, 6).map((t) => (
              <button key={t} type="button" className="knopflos" disabled={laeuft} onClick={() => dazu(t)}>
                <Tagchip name={'+ ' + t} farbe={tagfarbe.get(t)} />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Wiedervorlage({ wert, darf: darfEs, laeuft, setze }: {
  wert: string | null; darf: boolean; laeuft: boolean; setze: (w: string | null) => void;
}) {
  const plus = (tage: number) => {
    const d = new Date(heuteIso() + 'T12:00:00');
    d.setDate(d.getDate() + tage);
    return tagIso(d);
  };
  if (!darfEs) return <div className="klein">{wert ? datum(wert) : <span className="leise">keine</span>}</div>;
  return (
    <div>
      <div className="reihe" style={{ gap: 6 }}>
        <input type="date" value={wert ?? ''} disabled={laeuft} onChange={(e) => setze(e.target.value || null)} />
        {wert && <button type="button" className="knopf klein" disabled={laeuft} onClick={() => setze(null)}>weg</button>}
      </div>
      {wert && <div className="klein" style={{ marginTop: 4 }}>am {datum(wert)}</div>}
      <div className="reihe" style={{ gap: 4, marginTop: 6 }}>
        <button type="button" className="knopf klein" disabled={laeuft} onClick={() => setze(plus(1))}>morgen</button>
        <button type="button" className="knopf klein" disabled={laeuft} onClick={() => setze(plus(3))}>in 3 Tagen</button>
        <button type="button" className="knopf klein" disabled={laeuft} onClick={() => setze(plus(7))}>in 1 Woche</button>
      </div>
    </div>
  );
}

/**
 * Was die Kachel über einen Anhang wissen muss. Die Kennung ist die des
 * Dateibezugs und nicht die des Eingangs.
 */
function kacheldatei(x: any): Kacheldatei {
  return { id: String(x.datei_bezug_id ?? ''), dateiname: x.dateiname ?? '', mime: x.mime ?? null, groesse: x.groesse ?? null };
}

/** Ein Anhang mit seinem Befund - und dem Weg an einen eigenen Auftrag. */
function Anhang(
  { x, laeuft, darfZuordnen, oeffne, zeige, tu }: {
    x: any; laeuft: boolean; darfZuordnen: boolean;
    oeffne: (auftragId: string) => void;
    zeige: (d: Kacheldatei) => void;
    tu: (auftragId: string) => void;
  }
) {
  const [auf, setAuf] = useState(false);
  const k = kacheldatei(x);
  return (
    <div className="anhangzeile">
      <Dateikachel datei={k} oeffne={x.datei_bezug_id ? () => zeige(k) : undefined} />
      <div>
        <span className={'merker ' + (x.zustand === 'einsortiert' ? 'erledigt' : 'warnung')}>
          {x.zustand === 'einsortiert' ? 'abgelegt' : x.zustand}
        </span>
        <div className="klein leise">{x.art}</div>
        {x.grund && <div className="klein">{x.grund}</div>}
        {x.auftrag_id && (
          <button type="button" className="kartenweg klein" onClick={() => oeffne(x.auftrag_id)}>hängt am Auftrag &rarr;</button>
        )}
        {darfZuordnen && !auf && (
          <button type="button" className="knopf klein" style={{ marginTop: 4 }} disabled={laeuft} onClick={() => setAuf(true)}>
            {x.auftrag_id ? 'An einen anderen Auftrag' : 'An einen Auftrag hängen'}
          </button>
        )}
        {auf && (
          <ZuordnenKnopf laeuft={laeuft} offenVon ohneAufheben
                         tu={(auftragId) => { setAuf(false); if (auftragId) tu(auftragId); }} />
        )}
      </div>
    </div>
  );
}
